import { Box, Card, Typography, useMediaQuery } from '@mui/material'
import { jwtDecode } from 'jwt-decode'
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import coderScoutLogoUrl from '../../../src/assets/images/logo-with-wordmark-light.png'
import { getPublicCandidateReport } from '../../axios/api/candidate'
import OverlayLoading from '../../components/overlay-loading'
import { SettingsContext } from '../../context/setting'
import SEOComponent from '../../seo'
import { WEBSITE_URL } from '../../utils/constant'
import { getPublicToken, showErrorToast } from '../../utils/utils'
import CandidateEvaluationDetails from './CandidateEvaluationDetails'
import CandidateStagesDetail from './CandidateStagesDetail'

const CandidateReport = () => {
  //context
  const { settings } = useContext(SettingsContext)
  const isLargeScreen = useMediaQuery((theme) => theme.breakpoints.up('md'))

  // states
  const [stagesData, setStagesData] = useState(null)
  const [reportData, setReportData] = useState(null)
  const [loading, setLoading] = useState({
    candidateInfo: false,
    downloadResume: false
  })

  //variables
  const publicToken = useMemo(() => {
    return getPublicToken()
  }, [])

  const { contestId = null, candidateId = null } = useMemo(() => {
    if (publicToken) {
      return jwtDecode(publicToken)
    }
    return {}
  }, [publicToken])

  const tenantConfig = useMemo(
    () => settings?.tenantConfig || {},
    [settings?.tenantConfig]
  )

  const summaryPageLink = useMemo(() => {
    return `${
      reportData?.assessment ? 'assessments' : 'contests'
    }/${contestId}/candidate-summary/${candidateId}`
  }, [contestId, candidateId, reportData?.assessment])

  const handleDownloadResumeLoading = useCallback((value) => {
    setLoading((prev) => ({ ...prev, downloadResume: value }))
  }, [])

  const fetchCandidateInfo = useCallback(async () => {
    setLoading((prev) => ({ ...prev, candidateInfo: true }))
    const response = await getPublicCandidateReport()
    setLoading((prev) => ({ ...prev, candidateInfo: false }))

    if (response?.isError) {
      showErrorToast(response)
    } else {
      const { candidateStageDetails = [], ...rest } = response || {}
      setReportData(rest)
      setStagesData(candidateStageDetails)
    }
  }, [])

  useEffect(() => {
    if (publicToken) {
      fetchCandidateInfo()
    }
  }, [fetchCandidateInfo, publicToken])
  return (
    <Fragment>
      <SEOComponent title={`Candidate Evaluation Results`} />
      <Box
        className={`candidate-information-wrapper ${
          isLargeScreen ? '' : 'mobile'
        }`}
      >
        <Card>
          <CandidateEvaluationDetails
            data={reportData}
            setLoading={handleDownloadResumeLoading}
            summaryPageLink={summaryPageLink}
            isLargeScreen={isLargeScreen}
          />
        </Card>
      </Box>
      <Box className="candidate-report" mt={4}>
        <CandidateStagesDetail
          data={stagesData}
          contestId={contestId}
          assessment={reportData?.assessment}
        />
      </Box>
      {!tenantConfig?.fullyWhiteLabel && (
        <>
          <Box
            sx={{
              textAlign: 'center',
              p: 3,
              color: 'var(--text-secondary)',
              my: 2
            }}
          >
            <Typography variant="body2">
              © {new Date().getFullYear()} CoderScout.io. All rights reserved.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px'
              }}
            >
              <Typography variant="body2"> Powered by </Typography>
              <a href={WEBSITE_URL} target="_blank" rel="noopener noreferrer">
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '140px',
                    height: '100%',
                    maxHeight: '80px',
                    cursor: 'pointer'
                  }}
                >
                  <img
                    src={coderScoutLogoUrl}
                    style={{
                      width: '100%',
                      objectFit: 'contain',
                      height: '100%'
                    }}
                    alt="CoderScout Logo"
                  />
                </Box>
              </a>
            </Box>
          </Box>
        </>
      )}

      {(loading.downloadResume || loading.candidateInfo) && <OverlayLoading />}
    </Fragment>
  )
}

export default CandidateReport
